import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { User } from './models/user';
import { Tenant } from './models/tenant';
import { Role } from './models/role';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { Auth } from '@angular/fire/auth';
import { ManagementService } from './services/management.service';
import {fetchAuthSession, fetchUserAttributes, getCurrentUser, signOut, GetCurrentUserOutput, AuthSession} from "aws-amplify/auth";

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  // Create subject and public observable of user profile data
  private userProfileSubject$ = new BehaviorSubject<any>(null);
  userProfile$ = this.userProfileSubject$.asObservable();

  public userSubject = new BehaviorSubject<User>(null);
  user$ = this.userSubject.asObservable();

  public tenantsSubject = new BehaviorSubject<Tenant[]>(null);
  tenants$ = this.tenantsSubject.asObservable();

  public rolesSubject = new BehaviorSubject<Role[]>(null);
  roles$ = this.rolesSubject.asObservable();
  // Create a local property for login status
  loggedIn: boolean = null;
  isBusy: boolean = false;
  private analytics = inject(Analytics);
  auth = inject(Auth);

  constructor(
    private router: Router,
    public managementService: ManagementService) {
  }

  async getUser$(): Promise<GetCurrentUserOutput> {
    const user = await getCurrentUser();
    const userAttribute = await this.getUserAttribute();
    this.userProfileSubject$.next(userAttribute);
    return user;
  }

  async getUserAttribute(): Promise<any> {
    return fetchUserAttributes();
  }

  async getCurrentSession(): Promise<AuthSession> {
    return await fetchAuthSession();
  }

  async isLoggedIn(): Promise<boolean> {
    try {
      const session: AuthSession = await this.getCurrentSession();
      this.loggedIn = !!session?.tokens;
      return this.loggedIn;

    } catch (e) {
      return false;
    }
  }

  /** Returns true if user details are already loaded in memory or sessionStorage. */
  hasUserDetails(): boolean {
    return !!(this.userSubject.value && this.rolesSubject.value);
  }

  /** Ensures user details are available. Uses in-memory cache, fetches from API if empty. */
  async ensureUserDetails(): Promise<void> {
    if (this.hasUserDetails()) return;
    await this.fetchUserDetails();
  }

  /** Fetches user details from API (always makes network calls). */
  async fetchUserDetails(): Promise<void> {
    const user = await this.getUser$();
    if (!user) return;

    return new Promise((resolve) => {
      this.managementService.getUserRequest(user.signInDetails.loginId).subscribe((user) => {
        this.userSubject.next(user);
        sessionStorage.setItem("user", JSON.stringify(user));

        this.managementService.getUserTenants(user.id).subscribe((tenants) => {
          tenants.sort((a, b) => a.name.localeCompare(b.name));
          this.tenantsSubject.next(tenants);
          sessionStorage.setItem("tenants", JSON.stringify(tenants));
        });

        this.managementService.getUserRoles(user.id).subscribe(roles => {
          this.rolesSubject.next(roles);
          sessionStorage.setItem("roles", JSON.stringify(roles));
          resolve();
        });
      });
    });
  }


  async logout(buttonClicked?: boolean) {
    // Ensure Auth0 client instance exists
    await Promise.all([
      signOut(),
      this.auth.signOut(),
      logEvent(this.analytics, 'logout', {})
    ]).then(() => {
      localStorage.clear();
      sessionStorage.clear();
      if(buttonClicked) {
        this.router.navigate(['/']);
      }
    });
  }

}
